/**
 * PageTimestampModeDefaultContext — lets a page declare its preferred
 * "Filter by" default (first_seen / last_seen) WITHOUT writing to the global
 * timestampMode state.
 *
 * Why this exists (PENG-14090):
 *   The global default is 'last_seen' and that fits the AI dashboards whose
 *   "Age" column shows last-seen. The Component Catalog's Age column shows
 *   first-seen, so a "Last 7 days" range there should surface newly discovered
 *   components, not components *touched* in the past week. Changing the global
 *   default would flip every other AI dashboard with it; this context carries
 *   a catalog-only default and the shared chrome (TimeRangeFilterControl
 *   popover + GlobalFilterBar chip) reads it through `useEffectiveTimestampMode`
 *   so the UI matches what the catalog actually sends on the wire.
 *
 * How the override is resolved (`useEffectiveTimestampMode`):
 *   1) If the user explicitly picked a mode in the popover
 *      (GlobalFilterState.timestampModeExplicit === true), their pick wins —
 *      the page default steps aside.
 *   2) Otherwise, the most recently registered page default wins.
 *   3) Otherwise, the global `timestampMode` field is returned verbatim
 *      (preserves 'last_seen' as the ultimate fallback for every other page).
 *
 * A Map (not a single slot) tracks registrations so a brief overlap during
 * route transitions — React 18 can mount the next page before unmounting the
 * previous one — can't drop the active page's default to undefined. Each
 * `useRegisterPageTimestampModeDefault` call owns a unique `useId()`-stamped
 * entry; the "latest wins" iteration below reads the Map's own insertion order
 * so a nested registration cleanly supersedes an outer one and reverts when it
 * unmounts.
 */

import type { ReactNode } from 'react'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react'

import type { TimestampMode } from '../types/filterTypes'

import { useGlobalFilter } from './GlobalFilterContext'

type PageTimestampModeDefaultContextValue = {
  // Keeping the Map in state (not a ref) is deliberate — consumers of
  // `usePageTimestampModeDefault` need to re-render when registrations change,
  // and a ref would skip that update.
  registrations: ReadonlyMap<string, TimestampMode>
  register: (id: string, mode: TimestampMode) => void
  unregister: (id: string) => void
}

const PageTimestampModeDefaultContext =
  createContext<PageTimestampModeDefaultContextValue | null>(null)

type PageTimestampModeDefaultProviderProps = {
  children: ReactNode
}

export const PageTimestampModeDefaultProvider = ({
  children,
}: PageTimestampModeDefaultProviderProps): React.JSX.Element => {
  const [registrations, setRegistrations] = useState<
    ReadonlyMap<string, TimestampMode>
  >(() => new Map())

  const register = useCallback((id: string, mode: TimestampMode) => {
    setRegistrations(prev => {
      // Rebuild the Map (shallow) so React sees a new reference and consumers
      // re-render. Re-registering the same id first deletes the old entry so
      // the "latest wins" iteration order below actually tracks the most-
      // recent change rather than the first-ever insertion.
      const next = new Map(prev)
      next.delete(id)
      next.set(id, mode)
      return next
    })
  }, [])

  const unregister = useCallback((id: string) => {
    setRegistrations(prev => {
      if (!prev.has(id)) return prev
      const next = new Map(prev)
      next.delete(id)
      return next
    })
  }, [])

  const value = useMemo<PageTimestampModeDefaultContextValue>(
    () => ({ registrations, register, unregister }),
    [registrations, register, unregister],
  )

  return (
    <PageTimestampModeDefaultContext.Provider value={value}>
      {children}
    </PageTimestampModeDefaultContext.Provider>
  )
}

/**
 * The latest page-registered default, or undefined if no page has registered.
 * Readers that need the "what should the toggle highlight?" value should use
 * `useEffectiveTimestampMode` instead — it folds in the user's explicit
 * choice and the global fallback. This is exposed for tests and for
 * downstream code that genuinely wants just the page default.
 */
export const usePageTimestampModeDefault = (): TimestampMode | undefined => {
  const ctx = useContext(PageTimestampModeDefaultContext)
  if (!ctx || ctx.registrations.size === 0) return undefined
  // Map iteration follows insertion order; take the last value.
  let latest: TimestampMode | undefined
  for (const mode of ctx.registrations.values()) latest = mode
  return latest
}

/**
 * Register a page-level default for the "Filter by" toggle (first_seen /
 * last_seen). Call this at the top of a page component — the hook handles
 * mount/unmount so the default is dropped the moment the page unmounts.
 *
 * The registration has no effect once the user explicitly picks a mode in the
 * popover (see `useEffectiveTimestampMode`): their pick wins everywhere.
 *
 * `mode = undefined` is a legal no-op so callers can gate behind a feature
 * flag or route without conditional-hook churn.
 */
export const useRegisterPageTimestampModeDefault = (
  mode: TimestampMode | undefined,
): void => {
  const ctx = useContext(PageTimestampModeDefaultContext)
  const id = useId()
  // Depend only on the stable register/unregister refs, NOT on the whole
  // context value: `ctx` carries `registrations` and gets a new identity on
  // every register/unregister call, which would re-run this effect on its own
  // state change and loop forever (register → re-render → effect → cleanup
  // unregister → register again → …). The two callbacks are useCallback-stable
  // in the provider, so pulling them out breaks the cycle while still giving
  // the hook a working subscription.
  const register = ctx?.register
  const unregister = ctx?.unregister
  useEffect(() => {
    if (!register || !unregister || !mode) return
    register(id, mode)
    return () => unregister(id)
  }, [register, unregister, id, mode])
}

/**
 * The effective "Filter by" mode for the active page — what the toggle should
 * highlight, the chip should label, and `useResolvedTimeFilterParams` should
 * send on the wire. Precedence:
 *
 *   1) User's explicit pick                 (filters.timestampModeExplicit)
 *   2) Page-registered default               (usePageTimestampModeDefault)
 *   3) Global timestampMode field            (falls back to 'last_seen')
 *
 * Rule 1 is non-overridable: once the user clicks either value in the popover,
 * their choice wins on every page from then on, including the catalog.
 */
export const useEffectiveTimestampMode = (): TimestampMode => {
  const { filters } = useGlobalFilter()
  const pageDefault = usePageTimestampModeDefault()
  if (filters.timestampModeExplicit) return filters.timestampMode
  return pageDefault ?? filters.timestampMode
}
